import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import RichTextEditor from "@/components/RichTextEditor";
import { toast } from "sonner";
import { Save, Mail, FileText, Truck, FileSignature, Link, ClipboardCheck, CheckCircle, RotateCcw, PackageCheck } from "lucide-react";

const VARS_INVOICE = [
  { v: "{{customer_name}}", desc: "Customer's name" },
  { v: "{{invoice_number}}", desc: "Invoice number" },
  { v: "{{invoice_date}}", desc: "Invoice date" },
  { v: "{{due_date}}", desc: "Due date" },
  { v: "{{total}}", desc: "Invoice total" },
  { v: "{{company_name}}", desc: "Your company name" },
  { v: "{{invoice_table}}", desc: "Full line-items table (injected automatically)" },
];

const VARS_STATEMENT = [
  { v: "{{customer_name}}", desc: "Customer's name" },
  { v: "{{company_name}}", desc: "Your company name" },
  { v: "{{period}}", desc: "Statement month (e.g. July 2026)" },
  { v: "{{total_outstanding}}", desc: "Total amount owed" },
  { v: "{{statement_table}}", desc: "Full invoice list table (injected automatically)" },
];

const VARS_DELIVERY_NOTE = [
  { v: "{{customer_name}}", desc: "Customer's name" },
  { v: "{{invoice_number}}", desc: "Delivery note / invoice number" },
  { v: "{{company_name}}", desc: "Your company name" },
];

const VARS_SIGNED_DELIVERY = [
  { v: "{{customer_name}}", desc: "Customer's name" },
  { v: "{{invoice_number}}", desc: "Delivery note / invoice number" },
  { v: "{{company_name}}", desc: "Your company name" },
  { v: "{{signed_by}}", desc: "Name of person who accepted delivery" },
  { v: "{{signed_date}}", desc: "Date delivery was accepted (DD/MM/YYYY)" },
];

const VARS_SIGN_REQUEST = [
  { v: "{{customer_name}}", desc: "Customer's name" },
  { v: "{{invoice_number}}", desc: "Delivery note / invoice number" },
  { v: "{{company_name}}", desc: "Your company name" },
  { v: "{{sign_link}}", desc: "The unique URL the customer clicks to sign" },
];

const VARS_ACCOUNT_FORM_LINK = [
  { v: "{{customer_name}}", desc: "Account / company name" },
  { v: "{{company_name}}", desc: "Your company name" },
  { v: "{{form_link}}", desc: "The unique URL the customer clicks to complete the application" },
];

const VARS_ACCOUNT_FORM_ACCEPTED = [
  { v: "{{customer_name}}", desc: "Account / company name" },
  { v: "{{trading_name}}", desc: "The trading name from the submitted application" },
  { v: "{{company_name}}", desc: "Your company name" },
];

const VARS_ACCOUNT_FORM_REVERTED = [
  { v: "{{customer_name}}", desc: "Account / company name" },
  { v: "{{company_name}}", desc: "Your company name" },
  { v: "{{form_link}}", desc: "The unique URL the customer clicks to update their application" },
];

const VARS_AUTO_INVOICE = [
  { v: "{{customer_name}}", desc: "Customer's name" },
  { v: "{{invoice_number}}", desc: "Invoice number" },
  { v: "{{company_name}}", desc: "Your company name" },
];

function VarsReference({ vars }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
      <p className="text-xs font-semibold text-[#5B5C5F] uppercase tracking-wider mb-2">Available Variables</p>
      <div className="flex flex-wrap gap-2">
        {vars.map(({ v, desc }) => (
          <span key={v} title={desc} className="font-mono text-xs bg-white border border-slate-200 rounded px-2 py-0.5 text-[#032352] cursor-default">
            {v}
          </span>
        ))}
      </div>
      <p className="text-xs text-[#5B5C5F] mt-2 italic">Hover to see description. These are replaced with real values when the email is sent.</p>
    </div>
  );
}

function TemplateEditor({ type, template, vars, onSave, saving }) {
  const [subject, setSubject] = useState(template.subject || "");
  const [body, setBody] = useState(template.body_html || "");

  useEffect(() => {
    setSubject(template.subject || "");
    setBody(template.body_html || "");
  }, [template]);

  return (
    <div className="space-y-5">
      <div>
        <Label className="text-sm font-medium text-[#032352]">Subject Line</Label>
        <Input
          data-testid={`${type}-subject-input`}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="e.g. Invoice {{invoice_number}} from {{company_name}}"
          className="mt-1"
        />
      </div>
      <div>
        <Label className="text-sm font-medium text-[#032352] mb-1 block">Email Body</Label>
        <RichTextEditor value={body} onChange={setBody} placeholder="Write your email body here..." />
      </div>
      <VarsReference vars={vars} />
      <div className="flex justify-end">
        <Button
          data-testid={`save-${type}-template`}
          onClick={() => onSave(type, subject, body)}
          disabled={saving}
          className="bg-[#032352] hover:bg-[#032352]/90"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Template"}
        </Button>
      </div>
    </div>
  );
}

function TemplateSectionWrapper({ icon: Icon, title, description, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 rounded-full bg-[#032352]/10 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-[#032352]" />
        </div>
        <div>
          <p className="font-semibold text-[#032352] text-sm">{title}</p>
          <p className="text-xs text-[#5B5C5F]">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

export default function EmailTemplates() {
  const [templates, setTemplates] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/email-templates");
    setTemplates(data);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (type, subject, body_html) => {
    setSaving(true);
    try {
      await api.put("/email-templates", { [type]: { subject, body_html } });
      toast.success("Template saved");
      load();
    } catch {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  if (!templates) {
    return (
      <div className="max-w-4xl mx-auto">
        <p className="text-[#5B5C5F]">Loading templates...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="email-templates-page">
      <h1 className="font-heading text-3xl font-bold text-[#032352]">Email Templates</h1>
      <p className="text-[#5B5C5F] mt-1 mb-6">
        Customise the subject and body of every email sent from the system.
      </p>

      <Tabs defaultValue="invoice">
        <TabsList className="mb-6 flex-wrap h-auto gap-1">
          <TabsTrigger value="invoice" data-testid="tab-invoice-template" className="flex items-center gap-1.5 text-xs">
            <FileText className="w-3.5 h-3.5" /> Invoice
          </TabsTrigger>
          <TabsTrigger value="statement" data-testid="tab-statement-template" className="flex items-center gap-1.5 text-xs">
            <Mail className="w-3.5 h-3.5" /> Statement
          </TabsTrigger>
          <TabsTrigger value="delivery_note" data-testid="tab-delivery-note-template" className="flex items-center gap-1.5 text-xs">
            <Truck className="w-3.5 h-3.5" /> Delivery Note
          </TabsTrigger>
          <TabsTrigger value="signed_delivery_note" data-testid="tab-signed-delivery-template" className="flex items-center gap-1.5 text-xs">
            <FileSignature className="w-3.5 h-3.5" /> Signed Delivery
          </TabsTrigger>
          <TabsTrigger value="sign_request" data-testid="tab-sign-request-template" className="flex items-center gap-1.5 text-xs">
            <Link className="w-3.5 h-3.5" /> Sign Request
          </TabsTrigger>
          <TabsTrigger value="account_form_link" data-testid="tab-account-form-link-template" className="flex items-center gap-1.5 text-xs">
            <ClipboardCheck className="w-3.5 h-3.5" /> Credit App Link
          </TabsTrigger>
          <TabsTrigger value="account_form_accepted" data-testid="tab-account-form-accepted-template" className="flex items-center gap-1.5 text-xs">
            <CheckCircle className="w-3.5 h-3.5" /> App Approved
          </TabsTrigger>
          <TabsTrigger value="account_form_reverted" data-testid="tab-account-form-reverted-template" className="flex items-center gap-1.5 text-xs">
            <RotateCcw className="w-3.5 h-3.5" /> App Returned
          </TabsTrigger>
          <TabsTrigger value="auto_invoice" data-testid="tab-auto-invoice-template" className="flex items-center gap-1.5 text-xs">
            <PackageCheck className="w-3.5 h-3.5" /> Auto Invoice
          </TabsTrigger>
        </TabsList>

        <TabsContent value="invoice">
          <TemplateSectionWrapper icon={FileText} title="Invoice Email" description="Sent when you click 'Send Invoice' on any invoice">
            <TemplateEditor type="invoice" template={templates.invoice} vars={VARS_INVOICE} onSave={handleSave} saving={saving} />
          </TemplateSectionWrapper>
        </TabsContent>

        <TabsContent value="statement">
          <TemplateSectionWrapper icon={Mail} title="Account Statement Email" description="Sent to customers when you click 'Send Statements'">
            <TemplateEditor type="statement" template={templates.statement} vars={VARS_STATEMENT} onSave={handleSave} saving={saving} />
          </TemplateSectionWrapper>
        </TabsContent>

        <TabsContent value="delivery_note">
          <TemplateSectionWrapper icon={Truck} title="Delivery Note Email" description="Sent when you manually email a delivery note from the Delivery page">
            <TemplateEditor type="delivery_note" template={templates.delivery_note} vars={VARS_DELIVERY_NOTE} onSave={handleSave} saving={saving} />
          </TemplateSectionWrapper>
        </TabsContent>

        <TabsContent value="signed_delivery_note">
          <TemplateSectionWrapper icon={FileSignature} title="Signed Delivery Note — Auto Email" description="Automatically sent to the customer when a delivery is digitally signed">
            <TemplateEditor type="signed_delivery_note" template={templates.signed_delivery_note} vars={VARS_SIGNED_DELIVERY} onSave={handleSave} saving={saving} />
          </TemplateSectionWrapper>
        </TabsContent>

        <TabsContent value="sign_request">
          <TemplateSectionWrapper icon={Link} title="Sign Request — Link Email" description="Sent when you click 'Send Sign Link' — client clicks the link to sign remotely">
            <TemplateEditor type="sign_request" template={templates.sign_request} vars={VARS_SIGN_REQUEST} onSave={handleSave} saving={saving} />
          </TemplateSectionWrapper>
        </TabsContent>

        <TabsContent value="account_form_link">
          <TemplateSectionWrapper icon={ClipboardCheck} title="Credit Application — Link Email" description="Sent when you click 'Send Credit Application' on an account — customer clicks to fill in the form">
            <TemplateEditor type="account_form_link" template={templates.account_form_link} vars={VARS_ACCOUNT_FORM_LINK} onSave={handleSave} saving={saving} />
          </TemplateSectionWrapper>
        </TabsContent>

        <TabsContent value="account_form_accepted">
          <TemplateSectionWrapper icon={CheckCircle} title="Credit Application — Approved Email" description="Sent (with signed PDF attached) when you accept a credit account application">
            <TemplateEditor type="account_form_accepted" template={templates.account_form_accepted} vars={VARS_ACCOUNT_FORM_ACCEPTED} onSave={handleSave} saving={saving} />
          </TemplateSectionWrapper>
        </TabsContent>

        <TabsContent value="account_form_reverted">
          <TemplateSectionWrapper icon={RotateCcw} title="Credit Application — Returned Email" description="Sent when you revert an application back to the customer asking them to amend and resubmit">
            <TemplateEditor type="account_form_reverted" template={templates.account_form_reverted} vars={VARS_ACCOUNT_FORM_REVERTED} onSave={handleSave} saving={saving} />
          </TemplateSectionWrapper>
        </TabsContent>

        <TabsContent value="auto_invoice">
          <TemplateSectionWrapper icon={PackageCheck} title="Auto Invoice Email" description="Shown after a delivery note is signed — sent if the customer or admin requests a copy of the invoice">
            <TemplateEditor type="auto_invoice" template={templates.auto_invoice || {}} vars={VARS_AUTO_INVOICE} onSave={handleSave} saving={saving} />
          </TemplateSectionWrapper>
        </TabsContent>
      </Tabs>
    </div>
  );
}
